import {
  Box,
  Button,
  MenuItem,
  Select,
  TextField,
  Typography,
  Tooltip,
  Autocomplete,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import DynamicTable from "../lab-dynamic-table";
import axios from "axios";
import { toast } from "react-toastify";
import { Category, FileDownload, Print } from "@mui/icons-material";
import * as xlsx from "xlsx";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { useSelector } from "react-redux";
import DateRangeSelector from "../DateRangeSelector";
import { formatCurrency } from "../../utils/numberUtils";

// Reusable button styles
const gradientButtonStyle = {
  minWidth: { xs: "auto", md: "80px" },
  fontSize: { xs: "12px", md: "12px" },
  fontWeight: "600",
  textTransform: "none",
  borderRadius: "8px",
  background: "linear-gradient(45deg, #1976d2 30%, #42a5f5 90%)",
  color: "#ffffff",
  boxShadow: "0 3px 5px 2px rgba(25, 118, 210, .3)",
  transition: "all 0.3s ease-in-out",
  "&:hover": {
    background: "linear-gradient(45deg, #1565c0 30%, #1e88e5 90%)",
    boxShadow: "0 5px 15px rgba(25, 118, 210, .4)",
    transform: "translateY(-1px)",
  },
};

// --- Dept & Proc Income Tab Component ---
const DeptProcIncomeTab = () => {
  const nowDate = new Date().toISOString().split("T")[0];
  const [dateRange, setDateRange] = useState("Today");
  const [fromDate, setFromDate] = useState(nowDate);
  const [toDate, setToDate] = useState(nowDate);
  const [loading, setLoading] = useState(false);
  const [invoiceData, setInvoiceData] = useState<any[]>([]);
  const [departments, setDepartments] = useState<any[]>([]);
  const [categories, setCategories] = useState<any[]>([]);
  const [selectedDepartment, setSelectedDepartment] = useState<any>(null);
  const [selectedCategory, setSelectedCategory] = useState<any>(null);

  // Pagination states
  const [page, setPage] = useState(0);
  const [count, setCount] = useState(100);
  const [total, setTotal] = useState(0);
  console.log(categories, "Categorieskkk");

  const profile = useSelector((state: any) => state.profileSlice.profile);
  const ProcedurePermissions =
    profile?.data?.accessConfigurations?.Account || {};

  const handleDateRangeChange = (value: string) => {
    setDateRange(value);
  };

  // Pagination handlers
  const handleChangePage = (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent> | null,
    newPage: number
  ) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setCount(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Fetch departments
  const fetchDepartments = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_ADMIN_API_URL}/acc-department/getAllByPagination`,
        {
          params: { pageNo: 1, count: 1000 },
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        }
      );
      if (response.data.isSuccess) {
        setDepartments(response.data.data || []);
      }
    } catch (err: any) {
      console.error("Error fetching departments:", err);
    }
  };

  // Fetch categories
  const fetchCategories = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_ADMIN_API_URL}/income-category/getAllCategories`,
        {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        }
      );
      if (response.data.isSuccess) {
        setCategories(response.data.data || []);
      }
    } catch (err: any) {
      console.error("Error fetching categories:", err);
    }
  };

  // Fetch department wise income data
  const fetchDepartmentWiseIncome = async (): Promise<void> => {
    setLoading(true);
    try {
      const params: any = {
        fromDate,
        toDate,
        pageNo: page + 1,
        count,
      };

      // Add filters if selected
      if (selectedDepartment) {
        params.departmentId = selectedDepartment._id;
      }
      if (selectedCategory) {
        params.categoryId = selectedCategory._id;
      }

      const response = await axios.get(
        `${process.env.REACT_APP_ADMIN_API_URL}/income/getDepartmentWiseIncome`,
        {
          params,
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        }
      );
      if (response.data.isSuccess) {
        setInvoiceData(response.data.data || []);
        setTotal(response.data.totalCount || 0);
      } else {
        toast.error(response.data.message || "Failed to fetch invoice data");
      }
    } catch (err: any) {
      toast.error(err.message || "Error fetching invoice data");
      setInvoiceData([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDepartments();
    fetchCategories();
  }, []);

  useEffect(() => {
    fetchDepartmentWiseIncome();
  }, [fromDate, toDate, selectedDepartment, selectedCategory, page, count]);

  const handleExcelExport = () => {
    if (!invoiceData || invoiceData.length === 0) {
      toast.warn("No data to export.");
      return;
    }
    const worksheetData = invoiceData.map(item => ({
      "PATIENT NAME": item.patientInfo?.patientName || "",
      MRN: item.patientInfo?.mrn || "",
      DEPARTMENT: item.department || "",
      PROCEDURE: item.procedure || "",
      DESCRIPTION: item.description || "",
      AMOUNT: item.amount || 0,
      DISCOUNT: item.discount || 0,
      "NET AMOUNT": item.netAmount || 0,
      "PAID AMOUNT": item.payment || 0,
      DUE: item.due || 0,
      "CREATED BY": item.createdBy || "",
      "UPDATED BY": item.updatedBy || "",
      "CREATED AT": new Date(item.createdAt).toLocaleDateString() || "",
    }));

    // Calculate totals
    const totalAmount = invoiceData.reduce((sum, item) => sum + (Number(item?.amount) || 0), 0);
    const totalDiscount = invoiceData.reduce((sum, item) => sum + (Number(item?.discount) || 0), 0);
    const totalNetAmount = invoiceData.reduce((sum, item) => sum + (Number(item?.netAmount) || 0), 0);
    const totalPayment = invoiceData.reduce((sum, item) => sum + (Number(item?.payment) || 0), 0);
    const totalDue = invoiceData.reduce((sum, item) => sum + (Number(item?.due) || 0), 0);

    // Add total row
    worksheetData.push({
      "PATIENT NAME": "",
      MRN: "",
      DEPARTMENT: "",
      PROCEDURE: "",
      DESCRIPTION: "TOTAL",
      AMOUNT: totalAmount,
      DISCOUNT: totalDiscount,
      "NET AMOUNT": totalNetAmount,
      "PAID AMOUNT": totalPayment,
      DUE: totalDue,
      "CREATED BY": "",
      "UPDATED BY": "",
      "CREATED AT": "",
    });

    const worksheet = xlsx.utils.json_to_sheet(worksheetData);
    const workbook = xlsx.utils.book_new();
    xlsx.utils.book_append_sheet(workbook, worksheet, "Dept_Proc_Income");
    xlsx.writeFile(workbook, `Dept_Proc_Income_${fromDate}_to_${toDate}.xlsx`);
  };

  const handlePrint = () => {
    if (!invoiceData || invoiceData.length === 0) {
      toast.warn("No data to print.");
      return;
    }

    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 8;
    let yPosition = margin;

    // Professional header with minimal, clean design
    doc
      .setFontSize(10)
      .setFont("helvetica", "bold")
      .setTextColor(25, 55, 85)
      .text("DEPARTMENT & PROCEDURE INCOME REPORT", pageWidth / 2, yPosition, {
        align: "center",
      });
    
    yPosition += 6;
    doc
      .setDrawColor(25, 55, 85)
      .setLineWidth(0.5)
      .line(margin, yPosition, pageWidth - margin, yPosition);

    // Subtle metadata section with smaller font
    yPosition += 3;
    doc
      .setFontSize(6.5)
      .setFont("helvetica", "normal")
      .setTextColor(80, 80, 80)
      .text(
        `Report Period: ${fromDate} to ${toDate} | Generated: ${new Date().toLocaleDateString()} ${new Date().toLocaleTimeString()}`,
        pageWidth / 2,
        yPosition,
        { align: "center" }
      );

    yPosition += 6;

    const head = [
      [
        "PATIENT NAME",
        "MRN",
        "DEPARTMENT",
        "PROCEDURE",
        "DESCRIPTION",
        "AMOUNT",
        "DISCOUNT",
        "NET AMOUNT",
        "PAID",
        "DUE",
        "CREATED BY",
        "UPDATED BY",
        "CREATED AT",
      ],
    ];

    const body = invoiceData?.map((item, index) => [
      item.patientInfo?.patientName || "",
      item.patientInfo?.mrn || "",
      item.department || "",
      item.procedure || "",
      item.description || "",
      formatCurrency(item.amount || 0),
      formatCurrency(item.discount || 0),
      formatCurrency(item.netAmount || 0),
      formatCurrency(item.payment || 0),
      formatCurrency(item.due || 0),
      item.createdBy || "",
      item.updatedBy || "",
      new Date(item.createdAt).toLocaleDateString() || "",
    ]);

    // Calculate totals for PDF
    const totalAmount = invoiceData.reduce((sum, item) => sum + (Number(item?.amount) || 0), 0);
    const totalDiscount = invoiceData.reduce((sum, item) => sum + (Number(item?.discount) || 0), 0);
    const totalNetAmount = invoiceData.reduce((sum, item) => sum + (Number(item?.netAmount) || 0), 0);
    const totalPayment = invoiceData.reduce((sum, item) => sum + (Number(item?.payment) || 0), 0);
    const totalDue = invoiceData.reduce((sum, item) => sum + (Number(item?.due) || 0), 0);

    // Add total row
    body.push([
      "",
      "",
      "",
      "",
      "TOTAL",
      formatCurrency(totalAmount),
      formatCurrency(totalDiscount),
      formatCurrency(totalNetAmount),
      formatCurrency(totalPayment),
      formatCurrency(totalDue),
      "",
      "",
      "",
    ]);

    // Clean, professional table styling
    autoTable(doc, {
      startY: yPosition,
      head,
      body,
      headStyles: { 
        fillColor: [25, 55, 85],
        textColor: [255, 255, 255],
        fontStyle: 'bold',
        fontSize: 6,
        halign: 'center',
        valign: 'middle',
        cellPadding: 2
      },
      bodyStyles: {
        fontSize: 5.5,
        textColor: [40, 40, 40],
        cellPadding: 1.8
      },
      columnStyles: {
        5: { halign: "right" },
        6: { halign: "right" },
        7: { halign: "right" },
      },
      alternateRowStyles: {
        fillColor: [248, 249, 250]
      },
      margin: { top: margin, right: margin, bottom: margin, left: margin },
      didParseCell: function(data) {
        // Professional total row with premium styling
        if (data.row.index === body.length - 1) {
          data.cell.styles.fillColor = [25, 55, 85];
          data.cell.styles.textColor = [255, 255, 255];
          data.cell.styles.fontStyle = 'bold';
          data.cell.styles.fontSize = 5.8;
          data.cell.styles.lineColor = [200, 200, 200];
          data.cell.styles.lineWidth = 0.3;
          (data.cell.styles as any).cellPadding = { top: 2.5, right: 2, bottom: 2.5, left: 2 };
        }
      },
      didDrawPage: function(data) {
        // Add subtle footer with page number
        const pageSize = doc.internal.pageSize;
        const pageCount = doc.getNumberOfPages();
        const pageNum = data.pageNumber;
        
        doc.setFontSize(5);
        doc.setTextColor(120, 120, 120);
        doc.text(
          `Page ${pageNum} of ${pageCount}`,
          pageSize.getWidth() / 2,
          pageSize.getHeight() - 4,
          { align: "center" }
        );
      }
    });

    doc.autoPrint();
    const blob = doc.output("blob");
    const blobUrl = URL.createObjectURL(blob);
    const iframe = document.createElement("iframe");
    iframe.style.position = "fixed";
    iframe.style.width = "0";
    iframe.style.height = "0";
    iframe.style.border = "none";
    document.body.appendChild(iframe);
    
    const cleanup = () => {
      if (document.body.contains(iframe)) {
        document.body.removeChild(iframe);
        URL.revokeObjectURL(blobUrl);
      }
      window.removeEventListener("focus", cleanup);
    };
    
    window.addEventListener("focus", cleanup);
    iframe.src = blobUrl;
    
    iframe.onload = () => {
      try {
        const win = iframe.contentWindow;
        if (win) {
          win.focus();
          win.print();
        }
      } catch (e) {
        console.error("Error triggering print dialog:", e);
        cleanup();
      }
    };
    
    // Fallback print trigger
    setTimeout(() => {
      try {
        const win = iframe.contentWindow;
        if (win && document.body.contains(iframe)) {
          win.focus();
          win.print();
        }
      } catch (e) {
        console.error("Fallback print attempt failed:", e);
        cleanup();
      }
    }, 1000);
  };
  const totalPaid = invoiceData.reduce(
    (sum, item) => sum + (item.payment || 0),
    0
  );

  // Calculate totals for the new row
  const totalAmount = invoiceData.reduce(
    (sum, item) => sum + (Number(item?.amount) || 0),
    0
  );
  const totalDiscount = invoiceData.reduce(
    (sum, item) => sum + (Number(item?.discount) || 0),
    0
  );
  const totalNetAmount = invoiceData.reduce(
    (sum, item) => sum + (Number(item?.netAmount) || 0),
    0
  );
  const totalPayment = invoiceData.reduce(
    (sum, item) => sum + (Number(item?.payment) || 0),
    0
  );
  const totalDue = invoiceData.reduce(
    (sum, item) => sum + (Number(item?.due) || 0),
    0
  );

  const tableData: any[] = invoiceData.map(item => {
    const d = item.createdAt ? new Date(item.createdAt) : null;
    const formatDateTime = (date: Date | null) => {
      if (!date) return "";
      const pad = (n: number) => n.toString().padStart(2, "0");
      return `${date.getFullYear()}/${pad(date.getMonth() + 1)}/${pad(
        date.getDate()
      )} ${pad(date.getHours())}:${pad(date.getMinutes())}`;
    };

    return [
      item.patientInfo?.patientName || "",
      item.patientInfo?.mrn || "",
      item.department || "",
      item.procedure || "",
      item.description || "",
      formatCurrency(item.amount || 0),
      formatCurrency(item.discount || 0),
      formatCurrency(item.netAmount || 0),
      formatCurrency(item.payment || 0),
      formatCurrency(item.due || 0),
      item.createdBy || "",
      item.updatedBy || "",
      formatDateTime(d) || "",
    ];
  });

  if (tableData.length > 0) {
    tableData.push([
      "",
      "",
      "",
      "",
      <Typography fontWeight="bold" fontSize="12px">TOTAL</Typography>,
      <Typography fontWeight="bold" fontSize="12px">{formatCurrency(totalAmount)}</Typography>,
      <Typography fontWeight="bold" fontSize="12px">{formatCurrency(totalDiscount)}</Typography>,
      <Typography fontWeight="bold" fontSize="12px">{formatCurrency(totalNetAmount)}</Typography>,
      <Typography fontWeight="bold" fontSize="12px">{formatCurrency(totalPayment)}</Typography>,
      <Typography fontWeight="bold" fontSize="12px">{formatCurrency(totalDue)}</Typography>,
      "",
      "",
      "",
    ]);
  }

  return (
    <Box>
      <Box
        display="flex"
        sx={{ background: "#ffffff", borderRadius: "8px" }}
        justifyContent="space-between"
        alignItems="center"
        paddingY={1}
        paddingX={1}
        flexWrap="wrap"
        gap={2}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            alignSelf: "flex-end",
          }}
        >
          <Tooltip
            title={
              ProcedurePermissions?.view_module
                ? "Export data to Excel file"
                : "No permission to export"
            }
            arrow
            placement="top"
          >
            <Button
              variant="contained"
              size="small"
              startIcon={<FileDownload />}
              onClick={
                ProcedurePermissions?.view_module
                  ? handleExcelExport
                  : () => toast.warn("You don't have permission to export data")
              }
              disabled={!ProcedurePermissions?.view_module}
              sx={{
                ...gradientButtonStyle,
                opacity: ProcedurePermissions?.view_module ? 1 : 0.5,
              }}
            >
              Excel
            </Button>
          </Tooltip>
          <Tooltip
            title={
              ProcedurePermissions?.view_module
                ? "Print invoice report"
                : "No permission to print"
            }
            arrow
            placement="top"
          >
            <Button
              variant="contained"
              size="small"
              startIcon={<Print />}
              onClick={
                ProcedurePermissions?.view_module
                  ? handlePrint
                  : () =>
                      toast.warn("You don't have permission to print reports")
              }
              disabled={!ProcedurePermissions?.view_module}
              sx={{
                ...gradientButtonStyle,
                opacity: ProcedurePermissions?.view_module ? 1 : 0.5,
              }}
            >
              Print
            </Button>
          </Tooltip>
        </Box>

        <Box
          display="flex"
          gap={1.5}
          alignItems="flex-end"
          flexWrap="wrap"
          justifyContent={{ xs: "flex-start", md: "flex-end" }}
          flexGrow={1}
        >
          {/* Procedure Filter */}
          <Box>
            <Typography sx={{ fontSize: "12px", fontWeight: "600", mb: 0.5 }}>
              Procedure
            </Typography>
            <Autocomplete
              size="small"
              value={selectedCategory}
              onChange={(event, newValue) => {
                setSelectedCategory(newValue);
                setPage(0); // Reset to first page when filter changes
              }}
              options={categories}
              getOptionLabel={option => option?.categoryName || ""}
              renderInput={params => (
                <TextField
                  {...params}
                  className="custom-input"
                  placeholder="Select Category"
                  sx={{ minWidth: "200px" }}
                />
              )}
              clearOnEscape
            />
          </Box>

          {/* Department Filter */}
          <Box>
            <Typography sx={{ fontSize: "12px", fontWeight: "600", mb: 0.5 }}>
              Department
            </Typography>
            <Autocomplete
              size="small"
              value={selectedDepartment}
              onChange={(event, newValue) => {
                setSelectedDepartment(newValue);
                setPage(0); // Reset to first page when filter changes
              }}
              options={departments}
              getOptionLabel={option => option?.department || ""}
              renderInput={params => (
                <TextField
                  {...params}
                  className="custom-input"
                  placeholder="Select Department"
                  sx={{ minWidth: "200px" }}
                />
              )}
              clearOnEscape
            />
          </Box>

          {/* Date Range Selector Component */}
          <DateRangeSelector
            dateRange={dateRange}
            fromDate={fromDate}
            toDate={toDate}
            onDateRangeChange={handleDateRangeChange}
            onFromDateChange={setFromDate}
            onToDateChange={setToDate}
          />
        </Box>
      </Box>
      <Box paddingX={1} sx={{ marginTop: "16px" }}>
        <DynamicTable
          headers={[
            "PATIENT NAME",
            "MRN",
            "DEPARTMENT",
            "PROCEDURE",
            "DESCRIPTION",
            "AMOUNT",
            "DISCOUNT",
            "NET AMOUNT",
            "PAID",
            "DUE",
            "CREATED BY",
            "UPDATED BY",
            "CREATED AT",
          ]}
          loading={loading}
          data={tableData}
          totalAmount={totalPaid}
          height={"60vh"}
          page={page}
          rowsPerPage={count}
          handleChangePage={handleChangePage}
          handleChangeRowsPerPage={handleChangeRowsPerPage}
          totalRows={total}
        />
      </Box>
    </Box>
  );
};

export default DeptProcIncomeTab;
